import React, { useState, useEffect } from 'react';

export const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const rejectAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };


  const savePreferences = (necessary: boolean, analytics: boolean, marketing: boolean) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary,
      analytics,
      marketing,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setShowDetails(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center p-4">
      <div className="bg-white rounded-t-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {!showDetails ? (
          // Vue simple
           <div className="p-6">
            <h3 className="text-xl font-bold mb-3">Nous utilisons des cookies</h3>
            <p className="text-gray-700 mb-4">
              Nous utilisons des cookies pour améliorer votre expérience sur notre site. 
              Les cookies essentiels sont nécessaires au fonctionnement du site. 
              Vous pouvez accepter tous les cookies, les refuser ou personnaliser vos préférences.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              En savoir plus dans notre{' '}
              <a href="/privacy" className="text-blue-600 hover:underline">
                Politique de Confidentialité
              </a>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={rejectAll}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Tout refuser
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-semibold"
              >
                Personnaliser
              </button>
              <button
                onClick={acceptAll}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                Tout accepter
              </button>
            </div>
          </div>
        ) : (
          // Vue détaillée
          <CookiePreferences 
            onSave={savePreferences}
            onBack={() => setShowDetails(false)}
          />
        )}
      </div>
    </div>
  );
};

interface CookiePreferencesProps {
  onSave: (necessary: boolean, analytics: boolean, marketing: boolean) => void;
  onBack: () => void;
}

const CookiePreferences: React.FC<CookiePreferencesProps> = ({ onSave, onBack }) => {
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  return (
    <div className="p-6">
      <button 
        onClick={onBack}
        className="text-blue-600 hover:underline mb-4 flex items-center"
      >
        ← Retour
      </button>

      <h3 className="text-2xl font-bold mb-4">Préférences de cookies</h3>
      
      <div className="space-y-4 mb-6">
        {/* Cookies essentiels */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-lg">Cookies essentiels</h4>
            <span className="text-sm bg-gray-200 px-3 py-1 rounded">Obligatoires</span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés. 
            Ils sont utilisés pour la sécurité, l'authentification et le maintien de vos préférences.
          </p>
          <p className="text-xs text-gray-500">
            Exemples : session, authentification, préférences de cookies
          </p>
        </div>

        {/* Cookies analytiques */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-lg">Cookies analytiques</h4>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site 
            pour améliorer l'expérience utilisateur.
          </p>
          <p className="text-xs text-gray-500">
            Exemples : Google Analytics, statistiques d'utilisation
          </p>
        </div>

        {/* Cookies marketing */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-lg">Cookies marketing</h4>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.marketing}
                onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Ces cookies sont utilisés pour vous proposer des publicités pertinentes. 
            Actuellement, nous n'utilisons pas de cookies marketing.
          </p>
          <p className="text-xs text-gray-500">
            Exemples : publicités ciblées, remarketing
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => onSave(true, false, false)}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
        >
          Refuser tout sauf essentiels
        </button>
        <button
          onClick={() => onSave(preferences.necessary, preferences.analytics, preferences.marketing)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
        >
          Enregistrer mes préférences
        </button>
      </div>
    </div>
  );
};